import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, updateDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import notificationService from '../../services/notificationService';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText } from '../../security/sanitize';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useFeedback } from '../../contexts/FeedbackContext';
import Breadcrumb from '../../components/common/Breadcrumb';

// Icons
import {
  Image,
  Check,
  AlertTriangle,
  Info,
  RefreshCw,
  Tag,
  AtSign,
  FileText
} from 'lucide-react';

const ContentDraftSubmission = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, showError } = useFeedback();
  
  const [existingDraft, setExistingDraft] = useState(null);
  const [isResubmission, setIsResubmission] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  
  const [formData, setFormData] = useState({
    plannedCaption: '',
    plannedHashtags: '',
    plannedMentions: '',
    additionalNotes: ''
  });
  
  const [contentFiles, setContentFiles] = useState([]);
  const [contentUrls, setContentUrls] = useState([]);
  const [contentUploading, setContentUploading] = useState(false);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!currentUser || !campaignId) {
        setLoading(false);
        return;
      }

      try {
        // Get campaign document
        const campaignRef = doc(db, 'campaigns', campaignId);
        const campaignSnap = await getDoc(campaignRef);
        
        if (!campaignSnap.exists()) {
          setLoading(false);
          return;
        }
        
        const campaignData = campaignSnap.data();
        
        // Get brand information
        const brandRef = doc(db, 'users', campaignData.brandId);
        const brandSnap = await getDoc(brandRef);
        let brandData = {};
        
        if (brandSnap.exists()) {
          brandData = brandSnap.data();
        }
        
        // Check if user is a participant
        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', campaignId),
          where('influencerId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          showError('You are not an approved participant for this campaign.', 'You need to apply and be approved before submitting content drafts.', 6000);
          navigate(`/influencer/campaigns/${campaignId}`);
          return;
        }
        
        const participantData = querySnapshot.docs[0].data();
        
        if (participantData.status !== 'approved') {
          showError(`Your application status is "${participantData.status}".`, 'Only approved participants can submit content drafts.', 6000);
          navigate(`/influencer/campaigns/${campaignId}`);
          return;
        }

        // Check for existing draft submission
        if (participantData.draftId) {
          try {
            const draftRef = doc(db, 'contentDrafts', participantData.draftId);
            const draftSnap = await getDoc(draftRef);
            
            if (draftSnap.exists()) {
              const draftData = draftSnap.data();
              setExistingDraft({ id: draftSnap.id, ...draftData });
              
              // Check if this is a resubmission (revision requested or rejected)
              if (draftData.status === 'rejected' || participantData.draftStatus === 'revision_requested') {
                setIsResubmission(true);
                setRevisionFeedback(participantData.draftFeedback || draftData.brandFeedback || '');
                
                // Pre-fill form with existing data
                setFormData({
                  plannedCaption: draftData.plannedCaption || '',
                  plannedHashtags: Array.isArray(draftData.plannedHashtags) ? draftData.plannedHashtags.join(', ') : (draftData.plannedHashtags || ''),
                  plannedMentions: Array.isArray(draftData.plannedMentions) ? draftData.plannedMentions.join(', ') : (draftData.plannedMentions || ''),
                  additionalNotes: draftData.additionalNotes || ''
                });
                
                // Set existing content URLs
                if (draftData.contentUrls && draftData.contentUrls.length > 0) {
                  setContentUrls(draftData.contentUrls);
                }
              } else if (draftData.status === 'pending') {
                // Already submitted and pending - show success message and redirect back
                showSuccess('Draft submitted successfully!', 'Your content draft is being reviewed by the brand. You will be notified once it is approved or if changes are needed.', 4000);
                setTimeout(() => {
                  navigate(`/influencer/campaigns/${campaignId}`);
                }, 2000);
                return;
              } else if (draftData.status === 'approved') {
                // Draft already approved - redirect back to campaign
                showSuccess('Draft already approved!', 'Your content draft has been approved. You can now post your content and submit proof.', 4000);
                setTimeout(() => {
                  navigate(`/influencer/campaigns/${campaignId}`);
                }, 2000);
                return;
              }
            }
          } catch (draftError) {
            // Silently skip — treat as no existing draft
          }
        }
        
        setCampaign({
          id: campaignSnap.id,
          title: campaignData.name || 'Unnamed Campaign',
          brand: {
            id: campaignData.brandId,
            name: brandData.displayName || brandData.companyName || 'Unknown Brand',
            logo: brandData.logo || brandData.profileImageUrl || '/assets/default-logo.png'
          },
          requirements: campaignData.contentGuidelines || 'No specific requirements provided',
          deadline: campaignData.endDate || 'No deadline specified',
          requiredHashtags: campaignData.requiredHashtags || [],
          requiredMentions: campaignData.requiredMentions || '',
          ...campaignData
        });
      } catch (error) {
        showError('Failed to load campaign details.', 'Please check your connection and try again.', 6000);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignDetails();
  }, [currentUser, campaignId, navigate, showError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setContentFiles(files);
    }
  };

  const uploadContentFiles = async () => {
    if (contentFiles.length === 0) return contentUrls; // Keep existing URLs if no new files

    setContentUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of contentFiles) {
        const fileRef = ref(storage, `contentDrafts/${currentUser.uid}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        uploadedUrls.push(downloadURL);
      }
      return uploadedUrls;
    } catch (error) {
      throw new Error('Failed to upload content files');
    } finally {
      setContentUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) return;
    
    // Get CSRF token
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Validate form
    const sanitizedCaption = sanitizeText(formData.plannedCaption);
    
    if (!sanitizedCaption || sanitizedCaption.trim() === '') {
      showError("Please provide your planned caption", 'A caption for your content is required.', 5000);
      return;
    }
    
    // For resubmission, allow keeping existing files
    if (!isResubmission && contentFiles.length === 0) {
      showError("Please upload at least one content file", 'Upload the video or photo you plan to post.', 5000);
      return;
    }
    
    // For resubmission, check if we have either new files or existing URLs
    if (isResubmission) {
      if (contentFiles.length === 0 && contentUrls.length === 0) {
        showError("Please upload at least one content file", 'Upload the video or photo you plan to post.', 5000);
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      // Upload new files if provided, otherwise keep existing URLs
      let finalContentUrls = contentUrls; // Keep existing if any
      
      if (contentFiles.length > 0) {
        finalContentUrls = await uploadContentFiles();
      }
      
      // Process hashtags and mentions
      const hashtagsArray = formData.plannedHashtags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      const mentionsArray = formData.plannedMentions
        .split(',')
        .map(mention => mention.trim())
        .filter(mention => mention.length > 0);
      
      let draftRef;
      
      if (isResubmission && existingDraft) {
        // Update existing draft document
        const existingDraftRef = doc(db, 'contentDrafts', existingDraft.id);
        await updateDoc(existingDraftRef, {
          plannedCaption: sanitizedCaption,
          plannedHashtags: hashtagsArray,
          plannedMentions: mentionsArray,
          additionalNotes: sanitizeText(formData.additionalNotes),
          contentUrls: finalContentUrls,
          status: 'pending',
          resubmittedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        draftRef = { id: existingDraft.id };
      } else {
        // Create new draft submission document
        draftRef = await addDoc(collection(db, 'contentDrafts'), {
          campaignId,
          influencerId: currentUser.uid,
          brandId: campaign.brand.id,
          plannedCaption: sanitizedCaption,
          plannedHashtags: hashtagsArray,
          plannedMentions: mentionsArray,
          additionalNotes: sanitizeText(formData.additionalNotes),
          contentUrls: finalContentUrls,
          status: 'pending',
          campaignTitle: sanitizeText(campaign.title),
          influencerName: sanitizeText(currentUser.displayName) || 'Influencer',
          influencerEmail: currentUser.email || '',
          influencerProfileImage: currentUser.photoURL || '',
          submittedAt: serverTimestamp()
        });
      }
      
      // Update campaign participant status
      const participantsRef = collection(db, 'campaignParticipants');
      const q = query(
        participantsRef,
        where('campaignId', '==', campaignId),
        where('influencerId', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const participantId = querySnapshot.docs[0].id;
        const participantRef = doc(db, 'campaignParticipants', participantId);
        
        const updateData = {
          draftStatus: 'submitted',
          draftId: draftRef.id
        };
        
        // For resubmission, clear revision fields
        if (isResubmission) {
          updateData.draftFeedback = '';
          updateData.draftRevisionRequestedAt = null;
          updateData.draftResubmissionDate = serverTimestamp();
        } else {
          updateData.draftSubmissionDate = serverTimestamp();
        }
        
        await updateDoc(participantRef, updateData);
      }
      
      // Send notification to brand
      try {
        await notificationService.createNotification(
          campaign.brand.id,
          'draft_submission',
          isResubmission ? 'Content Draft Resubmitted' : 'New Content Draft Submission',
          `${currentUser.displayName || 'Influencer'} has ${isResubmission ? 'resubmitted' : 'submitted'} a content draft for campaign: ${campaign.title}`,
          {
            relatedId: draftRef.id,
            relatedType: 'contentDraft',
            campaignId,
            influencerId: currentUser.uid
          }
        );
      } catch (notifError) {
        // Non-blocking — submission still succeeded
      }
      
      showSuccess("Content draft submitted successfully!", "Your content draft has been submitted and is now pending brand approval.", 4000);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/influencer/campaigns');
      }, 2000);
    } catch (error) {
      showError("Failed to submit content draft.", 'Please check your connection and try again.', 6000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!campaign) {
    return (
      <div className="pb-12 relative">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="flex justify-center mb-4">
            <FileText className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign Not Found</h3>
          <p className="text-gray-600 mb-4">The campaign you're looking for doesn't exist or you don't have access to it.</p>
          <Breadcrumb
            items={[
              { label: 'Campaigns', to: '/influencer/campaigns' },
              { label: 'Draft Submission' },
            ]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12 relative">
      {/* Header */}
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: 'Campaigns', to: '/influencer/campaigns' },
            { label: 'Campaign', to: `/influencer/campaigns/${campaignId}` },
            { label: 'Draft Submission' },
          ]}
        />
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isResubmission ? 'Resubmit Content Draft' : 'Submit Content Draft'}
            </h1>
            <p className="text-gray-600 mt-1">
              Submit your content for brand approval before posting
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <img
              src={campaign.brand.logo}
              alt={campaign.brand.name}
              className="h-10 w-10 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/assets/default-logo.png';
              }}
            />
            <div>
              <p className="text-sm font-medium text-gray-900">{campaign.brand.name}</p>
              <p className="text-sm text-gray-500">{campaign.title}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 2-COLUMN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ===== LEFT COLUMN (form) ===== */}
        <div className="lg:col-span-2 space-y-6">
          {/* Content Draft Form */}
          <div className="bg-white rounded-lg shadow">
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <CSRFToken />
              
              {/* Content Files */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Files <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Upload the video or photo you plan to post (before posting it live)
                </p>
                
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <Image className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="content-files" className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                        <span>Upload files</span>
                        <input
                          id="content-files"
                          name="content-files"
                          type="file"
                          className="sr-only"
                          multiple
                          accept="image/*,video/*"
                          onChange={handleContentFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, MP4 up to 50MB each</p>
                  </div>
                </div>
                
                {/* Show selected files */}
                {contentFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected Files:</p>
                    <ul className="text-sm text-gray-600">
                      {contentFiles.map((file, index) => (
                        <li key={index} className="flex items-center">
                          <Check className="h-4 w-4 text-green-500 mr-2" />
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Show existing URLs for resubmission */}
                {isResubmission && contentUrls.length > 0 && contentFiles.length === 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Current Files:</p>
                    <p className="text-sm text-gray-600">{contentUrls.length} file(s) uploaded</p>
                  </div>
                )}
              </div>

              {/* Planned Caption */}
              <div>
                <label htmlFor="plannedCaption" className="block text-sm font-medium text-gray-700 mb-2">
                  Planned Caption <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Write the exact caption you plan to use when posting
                </p>
                <textarea
                  id="plannedCaption"
                  name="plannedCaption"
                  rows={6}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Write your planned caption here..."
                  value={formData.plannedCaption}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Planned Hashtags */}
              <div>
                <label htmlFor="plannedHashtags" className="block text-sm font-medium text-gray-700 mb-2">
                  Planned Hashtags
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  List the hashtags you plan to include (comma-separated, without #)
                </p>
                <input
                  type="text"
                  id="plannedHashtags"
                  name="plannedHashtags"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="engagedmanila, sponsored, brandname"
                  value={formData.plannedHashtags}
                  onChange={handleInputChange}
                />
              </div>

              {/* Planned Mentions */}
              <div>
                <label htmlFor="plannedMentions" className="block text-sm font-medium text-gray-700 mb-2">
                  Planned Mentions
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  List the accounts you plan to mention (comma-separated, without @)
                </p>
                <input
                  type="text"
                  id="plannedMentions"
                  name="plannedMentions"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="brandname, engagedmanila"
                  value={formData.plannedMentions}
                  onChange={handleInputChange}
                />
              </div>

              {/* Additional Notes */}
              <div>
                <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Any additional information or questions for the brand
                </p>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  rows={3}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Optional notes or questions..."
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate(`/influencer/campaigns/${campaignId}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || contentUploading}
                  className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting || contentUploading ? (
                    <>
                      <LoadingSpinner size="xs" color="white" />
                      <span className="ml-2">{contentUploading ? 'Uploading...' : 'Submitting...'}</span>
                    </>
                  ) : (
                    isResubmission ? 'Resubmit Draft' : 'Submit Draft'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ===== RIGHT COLUMN (sidebar) ===== */}
        <div className="lg:col-span-1 space-y-6 lg:self-start lg:sticky lg:top-6 z-20">
          {/* Revision Feedback */}
          {isResubmission && revisionFeedback && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-orange-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-orange-800">Revision Requested</h3>
                  <p className="text-sm text-orange-700 mt-1 whitespace-pre-wrap">{revisionFeedback}</p>
                </div>
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <Info className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Pre-Approval Process</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Submit your content draft for brand approval before posting. Once approved, you'll receive instructions to post and submit final proof.
                </p>
              </div>
            </div>
          </div>

          {/* Campaign Requirements */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Campaign Requirements</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Content Guidelines</h4>
                <div className="text-sm text-gray-600 whitespace-pre-line">
                  {campaign.requirements}
                </div>
              </div>
              
              {campaign.requiredHashtags && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Tag className="h-4 w-4 text-gray-400 mr-1.5" />
                    Required Hashtags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      let hashtagsArray = [];
                      if (typeof campaign.requiredHashtags === 'string') {
                        hashtagsArray = campaign.requiredHashtags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
                      } else if (Array.isArray(campaign.requiredHashtags)) {
                        hashtagsArray = campaign.requiredHashtags;
                      }
                      
                      return hashtagsArray.map((hashtag, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          #{hashtag}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}
              
              {campaign.requiredMentions && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <AtSign className="h-4 w-4 text-gray-400 mr-1.5" />
                    Required Mentions
                  </h4>
                  <p className="text-sm text-gray-600">@{campaign.requiredMentions}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentDraftSubmission;
